/**
 * Audit Log Page - Master Tenant Enterprise Edition
 * 
 * Full-width responsive audit trail with glassmorphism design
 * Stats dashboard, advanced filters, timeline charts, and rich data table
 */

import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '@/lib/api';
import { AUTH_STORAGE_KEYS } from '@/constants/auth';
import { chartNum } from '@/lib/recharts-format';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@easyflow/ui-master/components/ui/dialog';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-master/components/ui/select';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  RadialBarChart,
  RadialBar
} from 'recharts';
import {
  Loader2,
  Download,
  Search,
  Filter,
  RefreshCw,
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Users,
  Activity,
  Eye,
  ChevronDown,
  ChevronUp,
  FileJson,
  Building2,
  Globe,
  MessageSquare,
  Zap,
  Server,
  Gauge,
  TrendingUp,
  BarChart3
} from 'lucide-react';

interface AuditLogResponse {
  success: boolean;
  data?: AuditLog[];
  pagination?: {
    page: number;
    totalPages: number;
    total: number;
  };
}

interface AuditStats {
  total: number;
  byAction: Record<string, number>;
  byResourceType: Record<string, number>;
  byStatusCode: Record<string, number>;
  byTenant: Record<string, number>;
  recentActivity: { time: string; count: number }[];
  errorRate: number;
  securityEvents: number;
  byServerNode: Record<string, number>;
  avgDurationByServer: Record<string, number>;
  errorsByServer: Record<string, number>;
  resourceByServer: { server: string; resource: string; count: number }[];
  hourlyByServer: { server: string; hour: string; count: number }[];
}

interface AuditLogMeta {
  reason?: string;
  source?: string;
  [k: string]: unknown;
}

interface AuditLogNewValues {
  __meta?: AuditLogMeta;
  [k: string]: unknown;
}

interface AuditLog {
  id: string;
  tenant_id: string;
  tenant_name?: string;
  user_id?: string;
  user_email?: string;
  action: string;
  resource_type: string;
  resource_id?: string;
  old_values?: Record<string, unknown> | null;
  new_values?: AuditLogNewValues | null;
  ip_address?: string;
  user_agent?: string;
  request_method?: string;
  request_path?: string;
  status_code?: number;
  error_message?: string;
  duration_ms?: number;
  server_node?: string;
  created_at: string;
}

interface Filters {
  tenantId?: string;
  userId?: string;
  action?: string;
  resourceType?: string;
  ipAddress?: string;
  reason?: string;
  startDate?: string;
  endDate?: string;
  statusCode?: string;
}

function getAuditReason(log: AuditLog): string | undefined {
  const raw = log.new_values?.__meta?.reason;
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

interface Tenant {
  id: string;
  name: string;
}

const ACTION_COLORS: Record<string, string> = {
  create: '#10b981',
  update: '#3b82f6',
  delete: '#ef4444',
  login: '#8b5cf6',
  logout: '#6366f1',
  view: '#06b6d4',
  export: '#f59e0b',
  default: '#64748b'
};

const PIE_COLORS = ['#38bdf8', '#a855f7', '#10b981', '#f59e0b', '#ef4444', '#6366f1'];

const VPS_COLORS: Record<string, string> = {
  'be1': '#a855f7',
  'be2': '#10b981',
  'be3': '#ec4899',
  'be4': '#14b8a6',
  'fe1': '#38bdf8',
  'fs1': '#f59e0b',
  'fs2': '#ef4444',
  'lb1': '#6366f1',
  'kam1': '#8b5cf6',
  'obs1': '#0ea5e9',
  'db1': '#84cc16',
  'db2': '#22c55e',
  'redis1': '#f43f5e',
  'unknown': '#64748b'
};

const VPS_COLOR_ARRAY = ['#38bdf8', '#a855f7', '#10b981', '#f59e0b', '#ef4444', '#6366f1', '#ec4899', '#14b8a6'];

// Topologia post-cutover LB-1 (2026-07-27): BE-1/BE-2 solo LAN dietro LB-1,
// FE-1 edge statico interno. Solo i nodi backend Node scrivono audit rows;
// gli altri compaiono a zero per completezza topologica.
const VPS_LABELS: Record<string, string> = {
  'be1': 'Backend Node · LAN 10.4.202.58 · dietro LB-1',
  'be2': 'Backend Node · LAN 10.4.202.52 · dietro LB-1',
  'be3': 'Video LiveKit · LAN 10.4.202.54',
  'be4': 'Video LiveKit · LAN 10.4.202.55',
  'fe1': 'Edge statico interno · LAN 10.4.202.50',
  'fs1': 'FreeSWITCH · LAN 10.4.202.51',
  'fs2': 'FreeSWITCH · LAN 10.4.202.61',
  'lb1': 'Edge web pubblico · 85.94.215.110',
  'kam1': 'SBC Kamailio + rtpengine · 85.94.215.109',
  'obs1': 'Observability + bastion · LAN 10.4.202.60',
  'db1': 'PostgreSQL Patroni leader · LAN 10.4.202.53',
  'db2': 'PostgreSQL Patroni replica · LAN 10.4.202.56',
  'redis1': 'Redis dedicato · LAN 10.4.202.57',
};

// Nodi mostrati sempre nella dashboard VPS, anche senza traffico nel periodo
// (topologia completa 12 VPS; solo be1/be2 generano audit rows realmente).
const VPS_EXPECTED_NODES = [
  'be1', 'be2', 'be3', 'be4', 'fe1', 'fs1', 'fs2',
  'lb1', 'kam1', 'obs1', 'db1', 'db2', 'redis1',
] as const;

// Fallback per righe storiche non ancora normalizzate dal backend
// (hostname OS tipo 'EDGVoip-BE-1', 'easyflowai-fs-1').
const NODE_PATTERN = /(fe|be|fs|db|kam|obs|lb|redis)[-_ ]?(\d+)/i;
function normalizeVpsNode(node: string): string {
  const m = NODE_PATTERN.exec(node);
  if (m && m[1] && m[2]) return `${m[1].toLowerCase()}${m[2]}`;
  return node.toLowerCase();
}

function getVpsLabel(node: string): string {
  const canonical = normalizeVpsNode(node);
  return VPS_LABELS[canonical] ?? node;
}

// Nome breve per legende/card: 'be1' → 'BE-1'
function getVpsShort(node: string): string {
  const canonical = normalizeVpsNode(node);
  const m = /^([a-z]+)(\d+)$/.exec(canonical);
  if (m && m[1] && m[2]) return `${m[1].toUpperCase()}-${m[2]}`;
  return node.toUpperCase();
}

// Tooltip recharts per serie VPS: nome breve + descrizione estesa
function vpsSeriesTooltipFormatter(value: unknown, name: unknown): [string, string] {
  const seriesName = typeof name === 'string' ? name : String(name ?? '');
  return [chartNum(value).toLocaleString(), `${seriesName} — ${getVpsLabel(seriesName)}`];
}

// Legenda compatta: nome breve + descrizione estesa nel title (tooltip nativo)
function vpsLegendFormatter(value: string): JSX.Element {
  return (
    <span title={getVpsLabel(value)} className="cursor-help">
      {getVpsShort(value)}
    </span>
  );
}

function getVpsColor(node: string, index: number): string {
  const direct = VPS_COLORS[normalizeVpsNode(node)];
  if (direct) return direct;
  return VPS_COLOR_ARRAY[index % VPS_COLOR_ARRAY.length] ?? '#888888';
}

export default function AuditLogPage() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditStats | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<Filters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [exporting, setExporting] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.tenantId) params.append('tenantId', filters.tenantId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);

      const response = await apiClient.get<AuditStats>(`/master/audit-log/stats?${params}`);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load audit stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [filters.tenantId, filters.startDate, filters.endDate]);

  const loadTenants = useCallback(async () => {
    try {
      const response = await apiClient.get<{ tenants: Tenant[] }>('/master/tenants');
      if (response.success && response.data) {
        setTenants(response.data.tenants || []);
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
    }
  }, []);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...Object.entries(filters).reduce((acc, [key, value]) => {
          if (value) acc[key] = value;
          return acc;
        }, {} as Record<string, string>)
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response: AuditLogResponse = await apiClient.get<AuditLog[]>(`/master/audit-log?${params}`);
      
      if (response.success) {
        setLogs(response.data || []);
        const pagination = response.pagination;
        setTotalPages(pagination?.totalPages || 1);
        setTotalEntries(pagination?.total || 0);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  }, [page, limit, filters, searchTerm]);

  useEffect(() => {
    void loadTenants();
  }, [loadTenants]);

  useEffect(() => {
    void loadStats();
  }, [loadStats]);

  useEffect(() => {
    void loadLogs();
  }, [loadLogs]);

  const handleExport = async (format: 'csv' | 'json' = 'csv') => {
    setExporting(true);
    try {
      const params = new URLSearchParams(
        Object.entries(filters).reduce((acc, [key, value]) => {
          if (value) acc[key] = value;
          return acc;
        }, {} as Record<string, string>)
      );
      if (searchTerm) params.set('search', searchTerm);

      const token = localStorage.getItem(AUTH_STORAGE_KEYS.TOKEN);
      const response = await fetch(`/api/master/audit-log/export?${params}&format=${format}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-log-${Date.now()}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export audit logs:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    setShowFilters(false);
    void loadLogs();
    void loadStats();
  };

  const handleClearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setPage(1);
    setShowFilters(false);
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getActionBadge = (action: string) => {
    const color = Object.entries(ACTION_COLORS).find(([key]) => action.toLowerCase().includes(key))?.[1] || ACTION_COLORS.default;
    return (
      <Badge 
        style={{ backgroundColor: `${color}20`, color, borderColor: color }}
        className="border font-medium"
      >
        {action}
      </Badge>
    );
  };

  const getStatusBadge = (statusCode?: number) => {
    if (!statusCode) return <Badge variant="outline">-</Badge>;
    
    if (statusCode >= 200 && statusCode < 300) {
      return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">{statusCode}</Badge>;
    }
    if (statusCode >= 400 && statusCode < 500) {
      return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">{statusCode}</Badge>;
    }
    if (statusCode >= 500) {
      return <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">{statusCode}</Badge>;
    }
    return <Badge variant="outline">{statusCode}</Badge>;
  };

  const getSeverityIcon = (log: AuditLog) => {
    if (log.status_code && log.status_code >= 500) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    if (log.status_code && log.status_code >= 400) {
      return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
    if (log.action.includes('delete') || log.action.includes('security')) {
      return <Shield className="h-4 w-4 text-purple-500" />;
    }
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  };

  const formatDuration = (ms?: number) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const prepareActionChartData = () => {
    if (!stats?.byAction) return [];
    return Object.entries(stats.byAction)
      .slice(0, 6)
      .map(([name, value]) => ({ name, value }));
  };

  const prepareTimelineData = () => {
    if (!stats?.recentActivity) return [];
    return stats.recentActivity.map(item => ({
      time: item.time,
      events: item.count
    }));
  };

  // Unione dati reali + nodi attesi della topologia (FS-1/FS-2 senza
  // backend Node compaiono a zero invece di sparire).
  const mergeExpectedNodes = (byServerNode: Record<string, number>): Record<string, number> => {
    const merged: Record<string, number> = { ...byServerNode };
    for (const node of VPS_EXPECTED_NODES) {
      if (!(node in merged)) merged[node] = 0;
    }
    return merged;
  };

  const prepareVpsDonutData = () => {
    if (!stats?.byServerNode) return [];
    return Object.entries(mergeExpectedNodes(stats.byServerNode))
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name: getVpsShort(name),
        fullLabel: getVpsLabel(name),
        shortName: name,
        value
      }));
  };

  const prepareVpsBarData = () => {
    if (!stats?.byServerNode) return [];
    return Object.entries(mergeExpectedNodes(stats.byServerNode))
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({
        name: getVpsShort(name),
        fullLabel: getVpsLabel(name),
        shortName: getVpsShort(name),
        requests: value,
        avgMs: stats.avgDurationByServer?.[name] || 0,
        errors: stats.errorsByServer?.[name] || 0,
        errorRate: value > 0 ? ((stats.errorsByServer?.[name] || 0) / value * 100).toFixed(1) : '0'
      }));
  };

  const prepareResourceByVpsData = () => {
    if (!stats?.resourceByServer) return [];
    const servers = [...new Set(stats.resourceByServer.map(r => r.server))];
    const resources = [...new Set(stats.resourceByServer.map(r => r.resource))];
    return resources.slice(0, 8).map(resource => {
      const row: Record<string, string | number> = { resource };
      servers.forEach(server => {
        const match = stats.resourceByServer.find(r => r.server === server && r.resource === resource);
        row[server] = match?.count || 0;
      });
      return row;
    });
  };

  const prepareHourlyByVpsData = () => {
    if (!stats?.hourlyByServer) return [];
    const hours = [...new Set(stats.hourlyByServer.map(r => r.hour))].sort();
    const servers = [...new Set(stats.hourlyByServer.map(r => r.server))];
    return hours.map(hour => {
      const row: Record<string, string | number> = { hour };
      servers.forEach(server => {
        const match = stats.hourlyByServer.find(r => r.server === server && r.hour === hour);
        row[server] = match?.count || 0;
      });
      return row;
    });
  };

  const vpsServers = stats?.byServerNode ? Object.keys(stats.byServerNode) : [];

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-cyan-500 to-purple-600">
                <Shield className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Audit Log</h1>
                <p className="text-muted-foreground dark:text-muted-foreground/70">Enterprise Security & Compliance Trail</p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* Tenant Filter */}
            <Select
              value={filters.tenantId || 'all'}
              onValueChange={(value) => {
                setFilters(prev => ({ ...prev, tenantId: value === 'all' ? undefined : value }));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[180px] bg-white/50 dark:bg-muted/50 backdrop-blur-sm border-border dark:border-slate-700">
                <Building2 className="w-4 h-4 mr-2 text-muted-foreground/70" />
                <SelectValue placeholder="All Tenants" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tenants</SelectItem>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
              <Input
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadLogs()}
                className="pl-9 bg-white/50 dark:bg-muted/50 backdrop-blur-sm border-border dark:border-slate-700"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(true)}
              className="border-border dark:border-slate-700"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {Object.keys(filters).filter(k => filters[k as keyof Filters]).length > 0 && (
                <Badge className="ml-2 bg-cyan-500 text-white">
                  {Object.keys(filters).filter(k => filters[k as keyof Filters]).length}
                </Badge>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => { void loadLogs(); void loadStats(); }}
              disabled={loading}
              className="border-border dark:border-slate-700"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <div className="flex gap-1">
              <Button
                onClick={() => handleExport('csv')}
                disabled={exporting || loading}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                CSV
              </Button>
              <Button
                variant="outline"
                onClick={() => handleExport('json')}
                disabled={exporting || loading}
                className="border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300"
              >
                <FileJson className="w-4 h-4 mr-2" />
                JSON
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <GlassCard className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-200 dark:border-cyan-800">
            <GlassCardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Total Events</p>
                  <p className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">
                    {statsLoading ? '-' : (stats?.total?.toLocaleString() || '0')}
                  </p>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">All time audit entries</p>
                </div>
                <div className="p-3 rounded-full bg-cyan-100 dark:bg-cyan-900/30">
                  <Activity className="h-6 w-6 text-cyan-600 dark:text-cyan-400" />
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-200 dark:border-green-800">
            <GlassCardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Success Rate</p>
                  <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                    {statsLoading ? '-' : `${(100 - (stats?.errorRate || 0)).toFixed(1)}%`}
                  </p>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">Operations completed</p>
                </div>
                <div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30">
                  <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-200 dark:border-red-800">
            <GlassCardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Error Rate</p>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                    {statsLoading ? '-' : `${(stats?.errorRate || 0).toFixed(1)}%`}
                  </p>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">Failed operations</p>
                </div>
                <div className="p-3 rounded-full bg-red-100 dark:bg-red-900/30">
                  <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>

          <GlassCard className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-200 dark:border-purple-800">
            <GlassCardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Security Events</p>
                  <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                    {statsLoading ? '-' : (stats?.securityEvents?.toLocaleString() || '0')}
                  </p>
                  <p className="text-xs text-muted-foreground dark:text-muted-foreground mt-1">Login & auth actions</p>
                </div>
                <div className="p-3 rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <Shield className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Timeline */}
          <GlassCard className="lg:col-span-2">
            <GlassCardHeader>
              <GlassCardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-cyan-500" />
                Activity Timeline
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              <div style={{ width: '100%', height: 256 }}>
                <ResponsiveContainer width="100%" height={256}>
                  <AreaChart data={prepareTimelineData()}>
                    <defs>
                      <linearGradient id="colorEvents" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#38bdf8" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#38bdf8" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="time" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(15, 23, 42, 0.9)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="events"
                      stroke="#38bdf8"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorEvents)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>

          {/* Actions Distribution */}
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-500" />
                Actions Distribution
              </GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              <div style={{ width: '100%', height: 256 }}>
                <ResponsiveContainer width="100%" height={256}>
                  <PieChart>
                    <Pie
                      data={prepareActionChartData()}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {prepareActionChartData().map((_, index) => (
                        <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'rgba(15, 23, 42, 0.9)',
                        border: 'none',
                        borderRadius: '8px',
                        color: '#fff'
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>
        </div>

        {/* VPS Infrastructure Dashboard */}
        {!statsLoading && stats?.byServerNode && Object.keys(stats.byServerNode).length > 0 && (
          <>
            <div className="flex items-center gap-3 mt-2">
              <div className="p-2 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-600">
                <Server className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">VPS Infrastructure</h2>
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Carichi di lavoro, latenza e distribuzione per nodo</p>
              </div>
            </div>

            {/* VPS Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {prepareVpsBarData().map((vps, idx) => (
                <GlassCard key={vps.shortName} className="border-l-4" style={{ borderLeftColor: getVpsColor(vps.shortName, idx) }}>
                  <GlassCardContent className="pt-5 pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getVpsColor(vps.shortName, idx) }} />
                        <span className="font-semibold text-foreground cursor-help" title={vps.fullLabel}>{vps.shortName}</span>
                      </div>
                      <Badge className="text-xs" style={{ backgroundColor: `${getVpsColor(vps.shortName, idx)}20`, color: getVpsColor(vps.shortName, idx) }}>
                        {vps.requests.toLocaleString()} req
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-lg font-bold text-foreground">{vps.avgMs}</p>
                        <p className="text-[10px] text-muted-foreground">Avg ms</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold text-red-500">{vps.errors}</p>
                        <p className="text-[10px] text-muted-foreground">Errori</p>
                      </div>
                      <div>
                        <p className="text-lg font-bold" style={{ color: parseFloat(vps.errorRate) > 15 ? '#ef4444' : parseFloat(vps.errorRate) > 5 ? '#f59e0b' : '#10b981' }}>{vps.errorRate}%</p>
                        <p className="text-[10px] text-muted-foreground">Err Rate</p>
                      </div>
                    </div>
                  </GlassCardContent>
                </GlassCard>
              ))}
            </div>

            {/* VPS Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Donut: Distribuzione Carico per VPS */}
              <GlassCard>
                <GlassCardHeader>
                  <GlassCardTitle className="flex items-center gap-2">
                    <Gauge className="h-5 w-5 text-indigo-500" />
                    Carico per VPS
                  </GlassCardTitle>
                </GlassCardHeader>
                <GlassCardContent>
                  <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={prepareVpsDonutData()}
                          cx="50%"
                          cy="50%"
                          innerRadius={55}
                          outerRadius={95}
                          paddingAngle={4}
                          dataKey="value"
                          label={(props: { value?: number; percent?: number; name?: string }) =>
                            (props.value ?? 0) > 0 ? `${props.name ?? ''} ${((props.percent ?? 0) * 100).toFixed(0)}%` : ''
                          }
                          labelLine={false}
                        >
                          {prepareVpsDonutData().map((entry, index) => (
                            <Cell key={`vps-cell-${index}`} fill={getVpsColor(entry.shortName, index)} strokeWidth={2} stroke="rgba(255,255,255,0.3)" />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: 'none', borderRadius: '10px', color: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                          formatter={(value: unknown, _name: unknown, entry: { payload?: { name?: string; fullLabel?: string } }) => [
                            `${chartNum(value).toLocaleString()} richieste`,
                            entry?.payload?.fullLabel
                              ? `${entry.payload.name} — ${entry.payload.fullLabel}`
                              : entry?.payload?.name ?? ''
                          ]}
                        />
                        <Legend
                          iconSize={10}
                          wrapperStyle={{ fontSize: '12px', lineHeight: '20px' }}
                          formatter={(value: string, entry: { payload?: { fullLabel?: string } }) => (
                            <span title={entry?.payload?.fullLabel ?? ''} className="cursor-help">
                              {value}
                            </span>
                          )}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </GlassCardContent>
              </GlassCard>

              {/* Bar: Risorse per VPS */}
              <GlassCard className="lg:col-span-2">
                <GlassCardHeader>
                  <GlassCardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-cyan-500" />
                    Distribuzione Risorse per VPS
                  </GlassCardTitle>
                </GlassCardHeader>
                <GlassCardContent>
                  <div style={{ width: '100%', height: 280 }}>
                    <ResponsiveContainer width="100%" height={280}>
                      <BarChart data={prepareResourceByVpsData()} layout="vertical" margin={{ left: 80, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f020" />
                        <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                        <YAxis dataKey="resource" type="category" stroke="#94a3b8" fontSize={11} width={70} />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: 'none', borderRadius: '10px', color: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                          formatter={vpsSeriesTooltipFormatter}
                        />
                        {vpsServers.map((server, idx) => (
                          <Bar key={server} dataKey={server} name={getVpsShort(server)} fill={getVpsColor(server, idx)} stackId="stack" radius={idx === vpsServers.length - 1 ? [0, 4, 4, 0] : undefined} />
                        ))}
                        <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} formatter={vpsLegendFormatter} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </GlassCardContent>
              </GlassCard>
            </div>

            {/* Timeline per VPS (stacked area) */}
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-500" />
                  Timeline Carichi per VPS (ultime 24h)
                </GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                <div style={{ width: '100%', height: 260 }}>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={prepareHourlyByVpsData()}>
                      <defs>
                        {vpsServers.map((server, idx) => (
                          <linearGradient key={`grad-${server}`} id={`colorVps${idx}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={getVpsColor(server, idx)} stopOpacity={0.4}/>
                            <stop offset="95%" stopColor={getVpsColor(server, idx)} stopOpacity={0.05}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f020" />
                      <XAxis dataKey="hour" stroke="#94a3b8" fontSize={11} />
                      <YAxis stroke="#94a3b8" fontSize={11} />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'rgba(15, 23, 42, 0.95)', border: 'none', borderRadius: '10px', color: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
                        formatter={vpsSeriesTooltipFormatter}
                      />
                      {vpsServers.map((server, idx) => (
                        <Area
                          key={server}
                          type="monotone"
                          dataKey={server}
                          name={getVpsShort(server)}
                          stroke={getVpsColor(server, idx)}
                          strokeWidth={2}
                          fillOpacity={1}
                          fill={`url(#colorVps${idx})`}
                          stackId="1"
                        />
                      ))}
                      <Legend iconSize={10} wrapperStyle={{ fontSize: '12px' }} formatter={vpsLegendFormatter} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </GlassCardContent>
            </GlassCard>

            {/* VPS Detail Table */}
            <GlassCard>
              <GlassCardHeader>
                <GlassCardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-purple-500" />
                  Dettaglio Nodi VPS
                </GlassCardTitle>
              </GlassCardHeader>
              <GlassCardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border dark:border-slate-700">
                        <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Nodo</th>
                        <th className="text-right p-3 font-semibold text-foreground dark:text-muted-foreground/60">Richieste Totali</th>
                        <th className="text-right p-3 font-semibold text-foreground dark:text-muted-foreground/60">% Carico</th>
                        <th className="text-right p-3 font-semibold text-foreground dark:text-muted-foreground/60">Latenza Media</th>
                        <th className="text-right p-3 font-semibold text-foreground dark:text-muted-foreground/60">Errori</th>
                        <th className="text-right p-3 font-semibold text-foreground dark:text-muted-foreground/60">Error Rate</th>
                        <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Distribuzione</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prepareVpsBarData().map((vps, idx) => {
                        const totalReqs = Object.values(stats?.byServerNode || {}).reduce((a, b) => a + b, 0);
                        const loadPct = totalReqs > 0 ? (vps.requests / totalReqs * 100).toFixed(1) : '0';
                        return (
                          <tr key={vps.shortName} className="border-b border-slate-100 dark:border-slate-800 hover:bg-muted/50/50 dark:hover:bg-slate-800/50">
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getVpsColor(vps.shortName, idx) }} />
                                <span className="font-medium text-foreground cursor-help" title={vps.fullLabel}>{vps.name}</span>
                              </div>
                            </td>
                            <td className="p-3 text-right font-mono font-medium text-foreground dark:text-muted-foreground/60">
                              {vps.requests.toLocaleString()}
                            </td>
                            <td className="p-3 text-right">
                              <span className="font-medium" style={{ color: getVpsColor(vps.shortName, idx) }}>{loadPct}%</span>
                            </td>
                            <td className="p-3 text-right">
                              <span className={`font-mono ${vps.avgMs > 500 ? 'text-red-500' : vps.avgMs > 200 ? 'text-yellow-500' : 'text-green-500'}`}>
                                {vps.avgMs}ms
                              </span>
                            </td>
                            <td className="p-3 text-right font-mono text-red-500">
                              {vps.errors.toLocaleString()}
                            </td>
                            <td className="p-3 text-right">
                              <Badge className={parseFloat(vps.errorRate) > 15 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' : parseFloat(vps.errorRate) > 5 ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'}>
                                {vps.errorRate}%
                              </Badge>
                            </td>
                            <td className="p-3">
                              <div className="w-full bg-muted rounded-full h-2.5">
                                <div
                                  className="h-2.5 rounded-full transition-all"
                                  style={{ width: `${loadPct}%`, backgroundColor: getVpsColor(vps.shortName, idx) }}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </GlassCardContent>
            </GlassCard>
          </>
        )}

        {/* Active Filters Display */}
        {Object.keys(filters).filter(k => filters[k as keyof Filters]).length > 0 && (
          <GlassCard>
            <GlassCardContent className="py-3">
              <div className="flex items-center justify-between">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">Active filters:</span>
                  {Object.entries(filters).map(([key, value]) => value && (
                    <Badge key={key} variant="secondary" className="bg-cyan-100 dark:bg-cyan-900/30 text-cyan-800 dark:text-cyan-300">
                      {key}: {value}
                    </Badge>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={handleClearFilters}>
                  Clear All
                </Button>
              </div>
            </GlassCardContent>
          </GlassCard>
        )}

        {/* Audit Logs Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center justify-between">
              <GlassCardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-muted-foreground" />
                Audit Entries
              </GlassCardTitle>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">Show:</span>
                  <Select value={limit.toString()} onValueChange={(v) => { setLimit(parseInt(v)); setPage(1); }}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <span className="text-sm text-muted-foreground dark:text-muted-foreground/70">
                  {totalEntries.toLocaleString()} total entries
                </span>
              </div>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
              </div>
            ) : logs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-muted-foreground dark:text-muted-foreground/70">
                <Shield className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">No audit logs found</p>
                <p className="text-sm">Try adjusting your filters or search criteria</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border dark:border-slate-700">
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60 w-8"></th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Timestamp</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Tenant</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Action</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Resource</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Motivo</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">User</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">IP Address</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">VPS</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Status</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Duration</th>
                      <th className="text-left p-3 font-semibold text-foreground dark:text-muted-foreground/60">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <React.Fragment key={log.id}>
                        <tr className="border-b border-slate-100 dark:border-slate-800 hover:bg-muted/50/50 dark:hover:bg-slate-800/50 transition-colors">
                          <td className="p-3">
                            {getSeverityIcon(log)}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground dark:text-muted-foreground/70 whitespace-nowrap">
                            {new Date(log.created_at).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="h-4 w-4 text-muted-foreground/70" />
                              <span className="text-sm truncate max-w-[120px]" title={log.tenant_id}>
                                {log.tenant_name || log.tenant_id?.slice(0, 8) || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            {getActionBadge(log.action)}
                          </td>
                          <td className="p-3">
                            <div className="text-sm text-foreground dark:text-muted-foreground/60">{log.resource_type}</div>
                            {log.resource_id && (
                              <div className="text-xs text-muted-foreground dark:text-muted-foreground font-mono truncate max-w-[150px]">
                                {log.resource_id}
                              </div>
                            )}
                          </td>
                          <td className="p-3">
                            {(() => {
                              const reason = getAuditReason(log);
                              if (!reason) {
                                return <span className="text-sm text-muted-foreground/50">—</span>;
                              }
                              return (
                                <div
                                  className="flex items-start gap-1.5 max-w-[220px]"
                                  title={reason}
                                >
                                  <MessageSquare className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" />
                                  <span className="text-sm text-foreground dark:text-muted-foreground/70 line-clamp-2 break-words">
                                    {reason}
                                  </span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Users className="h-4 w-4 text-muted-foreground/70" />
                              <span className="text-sm truncate max-w-[120px]" title={log.user_id || '-'}>
                                {log.user_email || log.user_id?.slice(0, 8) || '-'}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Globe className="h-4 w-4 text-muted-foreground/70" />
                              <span className="text-sm font-mono">{log.ip_address || '-'}</span>
                            </div>
                          </td>
                          <td className="p-3">
                            {log.server_node ? (
                              <Badge className="text-xs font-mono" style={{ 
                                backgroundColor: `${getVpsColor(log.server_node, 0)}15`, 
                                color: getVpsColor(log.server_node, 0),
                                borderColor: getVpsColor(log.server_node, 0)
                              }}>
                                <Server className="h-3 w-3 mr-1" />
                                {log.server_node}
                              </Badge>
                            ) : (
                              <span className="text-sm text-muted-foreground/70">-</span>
                            )}
                          </td>
                          <td className="p-3">
                            {getStatusBadge(log.status_code)}
                          </td>
                          <td className="p-3 text-sm text-muted-foreground dark:text-muted-foreground/70">
                            {formatDuration(log.duration_ms)}
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleRowExpansion(log.id)}
                              >
                                {expandedRows.has(log.id) ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setSelectedLog(log)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {expandedRows.has(log.id) && (
                          <tr className="bg-muted/50/50 dark:bg-muted/30">
                            <td colSpan={12} className="p-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <Label className="text-muted-foreground">Request Path</Label>
                                  <p className="font-mono text-foreground dark:text-muted-foreground/60 break-all">
                                    {log.request_path || '-'}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground">Request Method</Label>
                                  <p className="font-medium text-foreground dark:text-muted-foreground/60">
                                    {log.request_method || '-'}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground">User Agent</Label>
                                  <p className="text-foreground dark:text-muted-foreground/60 truncate" title={log.user_agent}>
                                    {log.user_agent?.slice(0, 50) || '-'}
                                  </p>
                                </div>
                                {log.error_message && (
                                  <div className="md:col-span-2 lg:col-span-4">
                                    <Label className="text-red-500">Error</Label>
                                    <p className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded">
                                      {log.error_message}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-border dark:border-slate-700">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || loading}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-2">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        variant={page === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setPage(pageNum)}
                        className={page === pageNum ? "bg-cyan-600" : ""}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || loading}
                >
                  Next
                </Button>
              </div>
            )}
          </GlassCardContent>
        </GlassCard>

        {/* Filter Dialog */}
        <Dialog open={showFilters} onOpenChange={setShowFilters}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5 text-cyan-500" />
                Advanced Filters
              </DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Tenant</Label>
                <Select
                  value={filters.tenantId || 'all'}
                  onValueChange={(v) => setFilters(f => ({ ...f, tenantId: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Tenants" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tenants</SelectItem>
                    {tenants.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>User ID</Label>
                <Input
                  value={filters.userId || ''}
                  onChange={(e) => setFilters(f => ({ ...f, userId: e.target.value }))}
                  placeholder="UUID..."
                />
              </div>
              <div className="space-y-2">
                <Label>Action</Label>
                <Select
                  value={filters.action || 'all'}
                  onValueChange={(v) => setFilters(f => ({ ...f, action: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="logout">Logout</SelectItem>
                    <SelectItem value="view">View</SelectItem>
                    <SelectItem value="export">Export</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Resource Type</Label>
                <Select
                  value={filters.resourceType || 'all'}
                  onValueChange={(v) => setFilters(f => ({ ...f, resourceType: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Resources" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Resources</SelectItem>
                    <SelectItem value="extension">Extension</SelectItem>
                    <SelectItem value="trunk">Trunk</SelectItem>
                    <SelectItem value="route">Route</SelectItem>
                    <SelectItem value="queue">Queue</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="tenant">Tenant</SelectItem>
                    <SelectItem value="ivr">IVR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status Code</Label>
                <Select
                  value={filters.statusCode || 'all'}
                  onValueChange={(v) => setFilters(f => ({ ...f, statusCode: v === 'all' ? undefined : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="2xx">Success (2xx)</SelectItem>
                    <SelectItem value="4xx">Client Error (4xx)</SelectItem>
                    <SelectItem value="5xx">Server Error (5xx)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input
                  value={filters.ipAddress || ''}
                  onChange={(e) => setFilters(f => ({ ...f, ipAddress: e.target.value }))}
                  placeholder="192.168.1.1"
                />
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <Label>Motivo (testo libero)</Label>
                <Input
                  value={filters.reason || ''}
                  onChange={(e) => setFilters(f => ({ ...f, reason: e.target.value }))}
                  placeholder="es. reset password supporto, impersonation per debug…"
                />
              </div>
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="datetime-local"
                  value={filters.startDate || ''}
                  onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="datetime-local"
                  value={filters.endDate || ''}
                  onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={handleClearFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters} className="bg-cyan-600 hover:bg-cyan-700">
                Apply Filters
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Log Detail Dialog */}
        <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
          <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-cyan-500" />
                Audit Log Details
              </DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Timestamp</Label>
                    <p className="text-sm font-medium">{new Date(selectedLog.created_at).toLocaleString()}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Action</Label>
                    <div className="mt-1">{getActionBadge(selectedLog.action)}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Status</Label>
                    <div className="mt-1">{getStatusBadge(selectedLog.status_code)}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Duration</Label>
                    <p className="text-sm font-medium">{formatDuration(selectedLog.duration_ms)}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Resource Type</Label>
                    <p className="text-sm font-medium">{selectedLog.resource_type}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Resource ID</Label>
                    <p className="text-sm font-mono break-all">{selectedLog.resource_id || '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Tenant ID</Label>
                    <p className="text-sm font-mono break-all">{selectedLog.tenant_id}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">User ID</Label>
                    <p className="text-sm font-mono break-all">{selectedLog.user_id || '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">IP Address</Label>
                    <p className="text-sm font-mono">{selectedLog.ip_address || '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Server Node (VPS)</Label>
                    <p className="text-sm font-mono font-medium">{selectedLog.server_node ? getVpsLabel(selectedLog.server_node) : '-'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Request Method</Label>
                    <p className="text-sm font-medium">{selectedLog.request_method || '-'}</p>
                  </div>
                </div>

                {selectedLog.request_path && (
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Request Path</Label>
                    <p className="text-sm font-mono break-all mt-1">{selectedLog.request_path}</p>
                  </div>
                )}

                {selectedLog.user_agent && (
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">User Agent</Label>
                    <p className="text-sm break-all mt-1">{selectedLog.user_agent}</p>
                  </div>
                )}

                {(() => {
                  const reason = getAuditReason(selectedLog);
                  if (!reason) return null;
                  return (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                      <Label className="text-amber-700 dark:text-amber-400 text-xs flex items-center gap-1.5">
                        <MessageSquare className="h-3.5 w-3.5" />
                        Motivo
                      </Label>
                      <p className="text-sm text-amber-900 dark:text-amber-200 mt-1 whitespace-pre-wrap break-words">
                        {reason}
                      </p>
                    </div>
                  );
                })()}

                {selectedLog.error_message && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                    <Label className="text-red-600 dark:text-red-400 text-xs">Error Message</Label>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">{selectedLog.error_message}</p>
                  </div>
                )}

                {selectedLog.old_values && (
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">Previous Values</Label>
                    <pre className="text-xs bg-muted dark:bg-slate-900 p-3 rounded mt-2 overflow-x-auto">
                      {JSON.stringify(selectedLog.old_values, null, 2)}
                    </pre>
                  </div>
                )}

                {selectedLog.new_values && (
                  <div className="p-3 rounded-lg bg-muted/50 dark:bg-muted/50">
                    <Label className="text-muted-foreground text-xs">New Values</Label>
                    <pre className="text-xs bg-muted dark:bg-slate-900 p-3 rounded mt-2 overflow-x-auto">
                      {JSON.stringify(selectedLog.new_values, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
